import React, { FC, memo, CSSProperties } from "react";

import { usePricingDisplayStyles } from "./PricingDisplay.styles";
import { Box, Paper } from "@mui/material";
import { AuctionTypeEnum } from "@/lib/utils/AuctionTypeEnum";
import { PrembidLocalStorage } from "@/lib/utils/PrembidLocalStorage";
import { useSelector } from "react-redux";
import { CurrencyLabel } from "../../composite/CurrencyLabel";
import { FaMoneyBill, FaTag } from "react-icons/fa";
import { palette } from "@/themes/tokens";

export interface PricingDisplayProps {
    containerStyle?: CSSProperties;
    auctionType: number;
    guidePrice?: number;
    overrideGuidePriceRulesAndAlwaysHide: boolean;
    registrationStatus: number;
    openingBid: number;
    highestBid: number;
    highestBidColour?: string;
    highestBidSuffix?: string;
    fontSize?: string | number;
    // The mobile app look: "detail" for the full lot card, "compact" for the grid card.
    variant?: "detail" | "compact";
}

const PricingDisplay: FC<PricingDisplayProps> = ({
    auctionType,
    guidePrice,
    overrideGuidePriceRulesAndAlwaysHide,
    registrationStatus,
    openingBid,
    highestBid,
    highestBidColour,
    highestBidSuffix,
    containerStyle,
    fontSize,
    variant,
}) => {
    const { settings } = useSelector((state: any) => state.settings);
    const { classes: { pricingDisplayContainer } } = usePricingDisplayStyles();

    const isTender = Number(auctionType) === Number(AuctionTypeEnum.Tender.toString());
    const isTimed = Number(auctionType) === Number(AuctionTypeEnum.Timed.toString());
    const isLoggedIn = !!PrembidLocalStorage.currentUser?.userId;
    const isRegistered = registrationStatus === 1;
    const hasGuidePrice = guidePrice && guidePrice > 0;
    const hasOpeningBid = openingBid >= 0;
    const hasHighestBid = highestBid > 0;
    const pricingDisplayHasContent = hasGuidePrice || (isTimed && hasOpeningBid);

    const getPaperComponent = (showGuidePrice) => {
        if (variant) return getModernComponent(showGuidePrice);
        return pricingDisplayHasContent ? (
            <Paper
                className={`${pricingDisplayContainer} text-primary`}
                style={containerStyle}
            >
                {showGuidePrice && (
                    <CurrencyLabel
                        style={{ fontSize }}
                        value={guidePrice}
                        prefixString="Guide Price: "
                    />
                )}

                {isTimed && hasOpeningBid && (
                    <CurrencyLabel
                        style={{ fontSize }}
                        textColour={hasHighestBid ? highestBidColour : settings?.Styles?.Primary}
                        value={hasHighestBid ? highestBid : openingBid}
                        prefixString={hasHighestBid ? "Highest Bid: " : "Opening Bid: "}
                        suffixString={hasHighestBid ? highestBidSuffix : ""}
                    />
                )}
            </Paper>
        ) : (
            <div className={pricingDisplayContainer} style={containerStyle} />
        );
    };

    // Same layout as the mobile PricingComponent.
    const getModernComponent = (showGuidePrice) => {
        if (!pricingDisplayHasContent) return <></>;
        const detail = variant === "detail";
        const size = detail ? 14 : 11;
        const bidColour = hasHighestBid ? highestBidColour : settings?.Styles?.Primary;
        const item = (icon, label, value, colour, suffix = "") => (
            <div style={{ display: "flex", flexDirection: detail ? "column" : "row", alignItems: detail ? "center" : "flex-start", paddingRight: detail ? 12 : 0 }}>
                {detail && <span style={{ color: colour, marginBottom: 4, display: "inline-flex" }}>{icon}</span>}
                <div style={{ display: "flex", flexDirection: detail ? "column" : "row", alignItems: "flex-start", gap: detail ? 0 : 3, fontSize: size, color: colour, lineHeight: 1.35 }}>
                    <span>{label}</span>
                    <CurrencyLabel style={{ fontSize: size }} textColour={colour} value={value} suffixString={suffix} />
                </div>
            </div>
        );
        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: detail ? "row" : "column",
                    alignItems: "flex-start",
                    justifyContent: detail ? "flex-start" : "center",
                    gap: detail ? 4 : 0,
                    padding: detail ? 0 : "2px 4px",
                    minHeight: detail ? undefined : 40,
                    border: detail ? 0 : "1px solid #FFA500",
                    borderRadius: detail ? 0 : 5,
                    ...containerStyle,
                }}
            >
                {showGuidePrice && item(<FaTag size={20} />, detail ? "Guide Price:" : "GP: ", guidePrice, palette.text)}
                {isTimed && hasOpeningBid && item(<FaMoneyBill size={20} />, detail ? (hasHighestBid ? "Highest Bid: " : "Opening Bid: ") : hasHighestBid ? "HB: " : "OB: ", hasHighestBid ? highestBid : openingBid, bidColour, hasHighestBid ? highestBidSuffix : "")}
            </div>
        );
    };

    if (isTender) {
        if (isLoggedIn && !isRegistered && !overrideGuidePriceRulesAndAlwaysHide) {
            return getPaperComponent(hasGuidePrice);
        }
        if (!isLoggedIn && !overrideGuidePriceRulesAndAlwaysHide) {
            return getPaperComponent(hasGuidePrice);
        }

        return getPaperComponent(false);
    }

    if (isLoggedIn) {
        if (isRegistered) {
            return getPaperComponent(false);
        }
        if (!overrideGuidePriceRulesAndAlwaysHide) {
            return getPaperComponent(hasGuidePrice);
        }
        return getPaperComponent(false);
    }

    if (!overrideGuidePriceRulesAndAlwaysHide) {
        return getPaperComponent(hasGuidePrice);
    }

    return getPaperComponent(false);
};

export default memo(PricingDisplay);
